/**
 * Universal Generate Full Video Button
 * Works with any video type via adapter pattern
 */

import { useCallback } from 'react';
import { Loader2, Film, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useServerFullVideoGenerator } from '@/hooks/useServerFullVideoGenerator';
import type { IGenerateFullVideoButtonProps } from '@/@types/fullVideoGenerator';

export function GenerateFullVideoButton({
  adapter,
  isReady,
  readyMessage,
  existingVideoUrl,
  buttonLabel = 'Generate Full Video',
}: IGenerateFullVideoButtonProps) {
  const { state, generateFullVideo, reset } = useServerFullVideoGenerator(adapter);

  const handleClick = useCallback(async () => {
    if (state.status === 'error') {
      reset();
      return;
    }
    await generateFullVideo();
  }, [generateFullVideo, state.status, reset]);

  // Render different states
  const isProcessing =
    state.status === 'downloading' ||
    state.status === 'normalizing' ||
    state.status === 'concatenating' ||
    state.status === 'uploading';

  if (isProcessing) {
    const statusLabels: Record<string, string> = {
      downloading: `Downloading videos (${state.videosProcessed}/${state.totalVideos})...`,
      normalizing: `Normalizing videos (${state.videosProcessed}/${state.totalVideos})...`,
      concatenating: 'Creating final video...',
      uploading: 'Saving video...',
    };

    return (
      <Button disabled className="min-w-50">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        {statusLabels[state.status]}
        {state.progress > 0 && ` ${state.progress}%`}
      </Button>
    );
  }

  if (state.status === 'completed' || existingVideoUrl) {
    return (
      <Button
        variant="outline"
        className="min-w-50 border-green-500 text-green-600 hover:bg-green-50"
        onClick={handleClick}
      >
        <CheckCircle className="mr-2 h-4 w-4" />
        {state.status === 'completed' ? 'Video Generated! Regenerate?' : 'Regenerate Video'}
      </Button>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="space-y-2">
        <Button variant="destructive" className="min-w-50" onClick={handleClick}>
          <AlertCircle className="mr-2 h-4 w-4" />
          Error - Click to Retry
        </Button>
        <p className="text-xs text-red-500">{state.error}</p>
      </div>
    );
  }

  if (!isReady) {
    return (
      <Button disabled className="min-w-50" title={readyMessage}>
        <Film className="mr-2 h-4 w-4" />
        {readyMessage || buttonLabel}
      </Button>
    );
  }

  return (
    <Button onClick={handleClick} className="min-w-50">
      <Film className="mr-2 h-4 w-4" />
      {buttonLabel}
    </Button>
  );
}
